// Router minimo del portal sobre history: la ruta vive en la URL
// (logic/navigation.ts) y React solo la refleja. Cambiar de seccion empuja
// historia, asi el boton atras del navegador vuelve a la seccion anterior;
// cambiar el filtro de planta la reemplaza, para no llenar la historia de
// cambios de filtro.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { hrefFor, routeFromLocation, viewFromLocation, type PortalRoute } from "../logic/navigation.js";

export interface RouteControls {
  readonly route: PortalRoute;
  /** Vista secundaria de la seccion (`?vista=`); `null` es la principal. */
  readonly view: string | null;
  readonly goTo: (path: string) => void;
  readonly setSite: (siteId: string | null) => void;
  /** Cambia la vista secundaria reemplazando la entrada de historia, como el filtro. */
  readonly setView: (view: string | null) => void;
}

function current(paths: readonly string[]): PortalRoute {
  return routeFromLocation(window.location.pathname, window.location.search, paths);
}

function currentView(): string | null {
  return viewFromLocation(window.location.search);
}

export function useRoute(paths: readonly string[]): RouteControls {
  // La lista de rutas puede llegar como array nuevo en cada render; la clave
  // la estabiliza para no re-suscribir popstate.
  const key = paths.join("\n");
  const stablePaths = useMemo(() => key.split("\n"), [key]);
  const [route, setRoute] = useState<PortalRoute>(() => current(stablePaths));
  const [view, setViewState] = useState<string | null>(() => currentView());
  const routeRef = useRef(route);
  const viewRef = useRef(view);

  const apply = useCallback((next: PortalRoute, nextView: string | null, mode: "push" | "replace") => {
    const href = hrefFor(next, nextView);
    if (`${window.location.pathname}${window.location.search}` !== href) {
      if (mode === "push") window.history.pushState(null, "", href);
      else window.history.replaceState(null, "", href);
    }
    routeRef.current = next;
    viewRef.current = nextView;
    setRoute(next);
    setViewState(nextView);
  }, []);

  useEffect(() => {
    const onPopState = (): void => {
      const next = current(stablePaths);
      routeRef.current = next;
      viewRef.current = currentView();
      setRoute(next);
      setViewState(viewRef.current);
    };
    onPopState();
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [stablePaths]);

  // Cambiar de seccion deja atras la vista secundaria: es de la seccion anterior.
  const goTo = useCallback(
    (path: string) => {
      apply({ path, siteId: routeRef.current.siteId }, null, "push");
    },
    [apply],
  );

  const setSite = useCallback(
    (siteId: string | null) => {
      apply({ path: routeRef.current.path, siteId }, viewRef.current, "replace");
    },
    [apply],
  );

  const setView = useCallback(
    (nextView: string | null) => {
      apply(routeRef.current, nextView, "replace");
    },
    [apply],
  );

  return { route, view, goTo, setSite, setView };
}
